import React from 'react';

import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import type { DmxResolution } from '@ledium/types';
import { DmxChannelMapper, type DmxChannelMapperRow } from '@ledium/ui-common';
import SearchIcon from '@mui/icons-material/Search';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';

type Scenario = 'copy' | 'link-sync' | 'link-independent';

export interface ZoneChannel {
    id: string;
    name: string;
    dmxAddress: number;
    resolution: DmxResolution;
}

interface ZoneWizardStep4Props {
    zoneId: string;
    channelMap: Record<string, number | undefined>;
    onChannelMapChange: (channelMap: Record<string, number | undefined>) => void;
    scenario: Scenario;
    onChannelsLoaded?: (channels: ZoneChannel[]) => void;
}

function ZoneWizardStep4({ zoneId, channelMap, onChannelMapChange, scenario, onChannelsLoaded }: ZoneWizardStep4Props) {
    const [channels, setChannels] = React.useState<ZoneChannel[]>([]);
    const [takenAddresses, setTakenAddresses] = React.useState<number[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [fetchError, setFetchError] = React.useState<string | null>(null);
    const [testingChannelId, setTestingChannelId] = React.useState<string | null>(null);

    const channelMapRef = React.useRef(channelMap);

    React.useEffect(() => {
        setLoading(true);
        setFetchError(null);
        Promise.all([lediumApi.get<ZoneChannel[]>(`zones/${zoneId}/lightChannels`), lediumApi.get<number[]>('lightChannels/occupiedAddresses')])
            .then(([zoneData, occupiedData]) => {
                if (!zoneData) {
                    return;
                }
                setChannels(zoneData);
                onChannelsLoaded?.(zoneData);
                if (occupiedData) {
                    setTakenAddresses(occupiedData);
                }
                const existing = channelMapRef.current;
                const missing: Record<string, number | undefined> = {};
                let hasMissing = false;
                for (const ch of zoneData) {
                    if (!(ch.id in existing)) {
                        missing[ch.id] = undefined;
                        hasMissing = true;
                    }
                }
                if (hasMissing) {
                    onChannelMapChange({ ...existing, ...missing });
                }
            })
            .catch(() => setFetchError(i18n('error_loading_data') as string))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zoneId]);

    function handleAutoAssign() {
        const taken = new Set(takenAddresses);
        const newMap: Record<string, number | undefined> = {};
        for (const ch of channels) {
            let addr = 1;
            while (taken.has(addr) && addr <= 512) {
                addr++;
            }
            if (addr <= 512) {
                newMap[ch.id] = addr;
                taken.add(addr);
            } else {
                newMap[ch.id] = undefined;
            }
        }
        onChannelMapChange(newMap);
    }

    async function handleTest(channelId: string, dmxAddress: number, resolution: number) {
        setTestingChannelId(channelId);
        try {
            await lediumApi.get(`lightChannels/testDmx/${dmxAddress}/${resolution}`);
        } finally {
            setTestingChannelId(null);
        }
    }

    if (loading) {
        return <CircularProgress />;
    }

    if (fetchError) {
        return <Alert severity="error">{fetchError}</Alert>;
    }

    const rows: DmxChannelMapperRow[] = channels.map((ch) => ({
        channelId: ch.id,
        name: ch.name,
        currentDmxAddress: ch.dmxAddress,
        resolution: ch.resolution
    }));

    return (
        <>
            {scenario === 'copy' ? null : (
                <Typography variant="h6" gutterBottom>
                    {i18n('copy_zone_link_sync_step4_heading') as string}
                </Typography>
            )}
            <Typography paragraph>{scenario === 'copy' ? i18n('copy_zone_step4_description') : i18n('copy_zone_link_sync_step4_description')}</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <Button variant="contained" startIcon={<SearchIcon />} onClick={handleAutoAssign} disabled={channels.length === 0}>
                    {i18n('copy_zone_step4_search_free') as string}
                </Button>
            </Box>
            <DmxChannelMapper
                channels={rows}
                value={channelMap}
                onChange={onChannelMapChange}
                onTest={handleTest}
                testingChannelId={testingChannelId}
                takenAddresses={takenAddresses}
            />
        </>
    );
}

export default ZoneWizardStep4;
